import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { splitCountdown } from "@/shared/lib";

/**
 * Сколько заявка ещё принимает перевод.
 *
 * Отсчёт идёт от `expires_at` — серверного момента, — но решение «истекло»
 * принимает не этот компонент: по нулю он зовёт `onExpire`, чтобы экран
 * перезапросил заявку и показал ТО, ЧТО РЕШИЛ СЕРВЕР. Тот же принцип, что у
 * лимитированного предложения: дедлайн принадлежит серверу. Иначе переведённые
 * назад часы устройства объявляли бы платёж живым, а переведённые вперёд —
 * мёртвым, и оба раза неправда.
 *
 * Тик раз в полминуты, а не в секунду: окно платежа по умолчанию семь суток,
 * и секундная стрелка на нём — это перерисовка экрана ради цифры, которую
 * никто не ждёт.
 */
const TICK_MS = 30_000;

export function ExpiryCountdown({
  expiresAt,
  onExpire,
}: {
  expiresAt: string;
  onExpire?: () => void;
}) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());

  const deadline = Date.parse(expiresAt);
  // NaN — дата не разобралась. Не повод объявлять платёж истёкшим: сервер про
  // срок знает лучше, а мы просто ничего не показываем. Считается ДО хуков,
  // но ветвление идёт после них — порядок хуков обязан быть неизменным.
  const known = !Number.isNaN(deadline);
  const secondsLeft = known ? Math.floor((deadline - now) / 1000) : 1;

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (secondsLeft > 0) return;
    onExpire?.();
  }, [secondsLeft, onExpire]);

  if (!known) return null;

  if (secondsLeft <= 0) {
    return (
      <span className="text-caption font-normal text-status-warning">
        {t("wallet.payment.expired")}
      </span>
    );
  }

  const { days, hours, minutes } = splitCountdown(secondsLeft);
  const label =
    days > 0
      ? t("wallet.payment.expiresIn", { days, hours })
      : hours > 0
        ? t("wallet.payment.expiresInHours", { hours, minutes })
        : t("wallet.payment.expiresInMinutes", { minutes });

  return <span className="text-caption font-normal text-text-secondary">{label}</span>;
}
